import Sidebar from '../../components/Sidebar';
import { Header } from '../../components/header';
import { useTranslation } from 'react-i18next';
import { SalaryNoticeSection } from '../../components/salary-notice-section.component';
import { HolidayPayReportSection } from '../../components/holiday-pay-report-section.component';
import { PensionReportSection } from '../../components/pensions.component';
import { useState } from 'react';
import { PillTabs } from '../../components/dashboard/PillTabs';
// Imported only for the .mi-hero class used by the new heading. Every other
// rule in this file is scoped under .manage-invoice-v2 and can't leak here.
import './../../scss/components/manage-invoice.style.scss';

/* Tiny sparkles icon for the hero badge. Inline so the page has no
   new icon-library dependency. */
const HeroSparkles = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
        <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
    </svg>
);

/* Render the title with the LAST word styled as a violet gradient brand
   accent — works for any locale. Single-word titles are fully styled. */
const renderHeroTitle = (title: string) => {
    const trimmed = (title || '').trim();
    if (!trimmed) return null;
    const idx = trimmed.lastIndexOf(' ');
    if (idx === -1) {
        return <span className="mi-hero__brand">{trimmed}</span>;
    }
    const prefix = trimmed.slice(0, idx);
    const last = trimmed.slice(idx + 1);
    return (
        <>
            {prefix}{' '}
            <span className="mi-hero__brand">{last}</span>
        </>
    );
};

// Lucide-style (MIT) inline icons for the pill tabs — professional, name-matching:
// banknote (salary), sun (holiday), piggy-bank (pension). They inherit
// `currentColor` from the active/inactive tab state.
const SalaryNoticeTabIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect width="20" height="12" x="2" y="6" rx="2" />
        <circle cx="12" cy="12" r="2" />
        <path d="M6 12h.01" />
        <path d="M18 12h.01" />
    </svg>
);

const HolidayPayTabIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2" />
        <path d="M12 20v2" />
        <path d="m4.93 4.93 1.41 1.41" />
        <path d="m17.66 17.66 1.41 1.41" />
        <path d="M2 12h2" />
        <path d="M20 12h2" />
        <path d="m6.34 17.66-1.41 1.41" />
        <path d="m19.07 4.93-1.41 1.41" />
    </svg>
);

const PensionsTabIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M19 5c-1.5 0-2.8 1.4-3 2-3.5-1.5-11-.3-11 5 0 1.8 0 3 2 4.5V20h4v-2h3v2h4v-4c1-.5 1.7-1 2-2h2v-4h-2c0-1-.5-1.5-1-2V5z" />
        <path d="M2 9v1c0 1.1.9 2 2 2h1" />
        <path d="M16 11h.01" />
    </svg>
);

function MySalaryNoticePage() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('salary-notice');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const tabs = [
        { key: 'salary-notice', label: t('SALARY_LABEL_KEY'), icon: <SalaryNoticeTabIcon /> },
        { key: 'holiday-pay', label: t('HOLIDAY_PAY_LABEL_KEY'), icon: <HolidayPayTabIcon /> },
        { key: 'pensions', label: t('PENSIONS'), icon: <PensionsTabIcon /> }
    ];

    const renderContent = () => {
        switch (activeTab) {
            case 'salary-notice':
                return <SalaryNoticeSection />;

            case 'holiday-pay':
                return <HolidayPayReportSection />;

            case 'pensions':
                return <PensionReportSection />;
            default:
                return <SalaryNoticeSection />;
        }
    };

    return (
        <div className="dashboard-page salary-notice-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId='salary-notice'
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                {isSidebarOpen && (
                    <div
                        className="sidebar-overlay"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}

                <div className="dashboard-main-content">
                    <Header
                        setIsSidebarOpen={setIsSidebarOpen}
                    />
                    {/* Hero header — same soft violet card used by My Customers,
                        Drafts, My Freelancers, Deductions and Statements. .mi-hero
                        lives at top level in manage-invoice.style.scss so this page
                        just consumes it without needing a .manage-invoice-v2 wrapper.
                        Outer px-3 / pt-3 padding preserved so the card aligns with
                        the tab + section content below. */}
                    <div className='px-3 pt-3'>
                        <div className="mi-hero">
                            <span className="mi-hero__badge">
                                <HeroSparkles />
                                {t('EARNINGS_CENTER', { defaultValue: 'Earnings Center' })}
                            </span>
                            <h1 className="mi-hero__title">
                                {renderHeroTitle(t('MY_EARNINGS'))}
                            </h1>
                            <p className="mi-hero__subtitle">
                                {t('MY_EARNINGS_SUBTITLE')}
                            </p>
                        </div>
                    </div>

                    <div className='px-3 pt-2 sn-reveal' style={{ animationDelay: '90ms' }}>
                        <PillTabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />
                    </div>

                    <div className="px-3  gap-3 sn-reveal" style={{ animationDelay: '160ms' }}>
                        <div className="tab-content mt-3">
                            {renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MySalaryNoticePage;


